import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import backgroundImage from '../../images/landing-bg.png';
import rightSideImage from '../../images/hero-right-bg.png';
import mobileBg from '../../images/hero-mobile.png';
import { DoubleTickIcon } from 'icons';
import { Button } from 'components';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';

export const HeroSection: React.FC = () => {
  const { t, i18n } = useTranslation(); // Add i18n to access current language
  const navigate = useNavigate();
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Determine text size for lg breakpoint based on language
  const featureTextSize =
    i18n.language === 'en' ? 'lg:text-[20px]' : 'lg:text-[18px]';

  return (
    <>
      <Helmet>
        <title>Sonnen Raffstoren — Home</title>
        <meta
          name="description"
          content="Discover Sonnen Raffstoren GmbH’s premium external blinds, Markisen, Insektenschutz that blend robust engineering with refined design to protect buildings from excessive heat and glare while enhancing energy efficiency. Our home page highlights key solutions, showcases flagship models and explains how exterior blinds can reduce cooling demand, improve occupant comfort and contribute to a sustainable building strategy. We provide tailored advice, on-site assessments and professional installation services. Options include manual and motorized drives, smart-home compatibility and a variety of finishes to suit architectural styles. Learn about our commitment to durable materials, local service and aftercare support. Whether you are an architect, builder or homeowner, Sonnen Raffstoren offers comprehensive shading systems designed to meet Swiss climatic conditions and project specifications, from initial consultation to final commissioning and maintenance."
        />
        <html>{`
        <div style={{ display: 'none' }}>
          <h1>Sonnen Raffstoren GmbH in Switzerland</h1>
          <p>
            Sonnen Raffstoren GmbH is a Swiss-based provider of premium external
            shading systems, serving homeowners, architects and businesses
            across Switzerland. Our solutions include raffstoren, roller
            shutters and custom outdoor shading built for Swiss weather
            conditions, long service life and reliable daily use. We support
            projects with on-site measurements, tailored planning, professional
            installation and aftercare service. Options include motorized
            drives, wind and sun sensors and smart control integration to
            improve comfort and reduce cooling demand. We offer multiple lamella
            profiles, finishes and RAL color matching to align with Swiss
            architectural styles and façade requirements. This product page
            helps you understand technical parameters, available colors and
            suitable configuration choices for residential and commercial
            applications in Switzerland.
          </p>
        </div>
        <div style={{ display: 'none' }} lang="en">
          <h1>
            Sonnen Raffstoren GmbH — Premium External Shading in Switzerland
          </h1>

          <p>
            Sonnen Raffstoren GmbH is a specialized provider of high-quality
            external shading systems in
            <strong> Switzerland</strong>, focusing on <em>raffstoren</em>,
            roller shutters, and bespoke outdoor sun protection for residential
            and commercial buildings. Our mission is simple: deliver durable,
            precisely engineered shading solutions that improve comfort, reduce
            overheating, and support energy-efficient building strategies across
            Switzerland — from lakeside homes to alpine locations where wind and
            weather demand robust construction.
          </p>

          <h2>What We Do</h2>
          <p>
            We design, supply, and install tailor-made shading systems that
            combine function and architecture. From initial consultation and
            on-site measurements to commissioning and aftercare, we manage the
            full process with transparent timelines and clear service
            conditions. Our products are built for the Swiss climate, helping to
            control glare, protect privacy, and optimize daylight without
            compromising aesthetics.
          </p>

          <h3>Core Product Categories</h3>
          <ul>
            <li>
              <strong>Raffstoren:</strong> exterior venetian blinds with
              adjustable slats for precise daylight control and effective solar
              heat reduction.
            </li>
            <li>
              <strong>Rollläden:</strong> roller shutters that enhance security,
              insulation, and night-time privacy for homes and businesses in
              Switzerland.
            </li>
            <li>
              <strong>Custom Outdoor Shading:</strong> made-to-measure façade
              shading for demanding architectural projects and special
              installation conditions.
            </li>
          </ul>

          <h3>Options & Add-ons</h3>
          <p>
            Every project can be configured to match the façade and the
            performance targets. Choose different slat profiles, colors, and
            surface finishes — including RAL matching — so the system blends
            into Swiss architectural styles, whether classic or modern. For
            tougher exposure, we offer weatherproof guidance rails, wind-stable
            solutions, and durable coatings engineered for long-term
            reliability.
          </p>

          <h4>Motorization, Sensors & Smart Control</h4>
          <p>
            For maximum comfort, we provide electrical drives and automation
            features such as wind and sun sensors. Smart control options allow
            integration into building automation and smart-home systems commonly
            used in Switzerland, enabling automated shading routines that
            respond to daylight and weather conditions. This improves user
            comfort and can reduce cooling demand during hot summer days.
          </p>

          <h2>Quality You Can Measure</h2>
          <p>
            Quality is central to our approach. We work with carefully selected
            suppliers and focus on tested materials, corrosion protection, and
            precise manufacturing processes. Each system is checked for
            functionality, weather resistance, and ease of operation. Our
            installation and service teams are trained to ensure correct
            mounting and long-term performance — a key factor for projects in
            Switzerland where seasonal temperature swings and wind loads matter.
          </p>

          <h3>Sustainability & Energy Efficiency in Switzerland</h3>
          <p>
            External shading is not only about comfort — it is a practical step
            toward sustainable building performance. Properly planned raffstoren
            can reduce summer overheating while allowing useful daylight, which
            supports lower energy use. By minimizing reliance on air
            conditioning and improving thermal comfort, our shading solutions
            contribute to energy efficiency goals across Switzerland, including
            modern residential construction and large commercial façades.
          </p>

          <h4>Practical Benefits</h4>
          <ol>
            <li>
              <strong>Less heat gain:</strong> block direct solar radiation
              before it enters the room.
            </li>
            <li>
              <strong>More comfort:</strong> reduce glare while keeping spaces
              bright and usable.
            </li>
            <li>
              <strong>Lower cooling demand:</strong> support a sustainable,
              cost-effective climate strategy.
            </li>
            <li>
              <strong>Long service life:</strong> durable systems reduce
              replacement and resource use.
            </li>
          </ol>

          <h2>Service & Project Support</h2>
          <p>
            Sonnen Raffstoren GmbH supports both private homeowners and
            professional stakeholders such as architects, builders, and facility
            managers throughout Switzerland. We provide project-based
            coordination, realistic schedules, and traceable quality checks.
            Whether it’s a single-family home, multi-unit building, office
            façade, or public project, we help align technical requirements with
            design expectations.
          </p>

          <h3>Typical Workflow</h3>
          <ul>
            <li>
              <strong>Consultation:</strong> needs analysis and shading strategy
              for your Swiss property.
            </li>
            <li>
              <strong>On-site measurement:</strong> accurate dimensions for a
              perfect fit.
            </li>
            <li>
              <strong>Configuration:</strong> select profiles, finishes,
              motorization, sensors, and controls.
            </li>
            <li>
              <strong>Installation:</strong> clean, professional mounting and
              commissioning.
            </li>
            <li>
              <strong>Aftercare:</strong> maintenance, inspection, and repairs
              for long-term reliability.
            </li>
          </ul>

          <h4>Frequently Requested Solutions</h4>
          <p>
            Common requests in Switzerland include wind-stable guidance for
            exposed locations, smart automation for modern homes, and discreet
            color matching for architectural consistency. We can also advise on
            shading strategies that complement ventilation, glazing, and thermal
            mass — especially important in buildings designed for low energy
            consumption.
          </p>

          <h5>References & Continuous Improvement</h5>
          <p>
            We have delivered projects across a wide range of building types in
            Switzerland, and we actively use customer feedback to refine product
            details and service quality. This continuous improvement mindset
            supports our promise: deliver shading systems that feel premium in
            daily use and remain reliable for years.
          </p>

          <h2>Vision</h2>
          <p>
            Our vision is to be recognized in Switzerland as a trusted partner
            for sustainable external shading, combining design, engineering, and
            responsive service. We continue to invest in innovation — from
            quieter drives and improved slat profiles to modern IoT-ready
            control options — so our customers benefit from solutions that are
            both future-proof and resource-conscious.
          </p>

          <h3>Contact & Next Steps</h3>
          <p>
            If you are planning a renovation, a new build, or a façade project
            in Switzerland, contact Sonnen Raffstoren GmbH for a consultation.
            We will help you select the right raffstoren or roller shutter
            configuration, define a realistic project timeline, and provide
            clear warranty and service details.
          </p>

          <hr />

          <p>
            <strong>Sonnen Raffstoren GmbH</strong> — external shading systems
            engineered for Switzerland: durable, customizable, and designed to
            improve comfort and energy efficiency.
          </p>

          <small>
            Keywords: Switzerland, raffstoren, external blinds, roller shutters,
            outdoor shading, smart control, wind stability, RAL colors, energy
            efficiency, Romanshorn.
          </small>
        </div>
        <div style={{ display: 'none' }} lang="de">
          <h1>
            Sonnen Raffstoren GmbH – Hochwertiger Sonnenschutz in der Schweiz
          </h1>

          <p>
            <strong>Sonnen Raffstoren GmbH</strong> ist ein spezialisiertes
            Unternehmen für
            <em>
              Raffstoren, Rollläden und maßgeschneiderte
              Außenbeschattungssysteme
            </em>
            mit Sitz in der <strong>Schweiz</strong>. Wir bieten Lösungen für
            Wohn- und Gewerbeimmobilien, die Schutz vor Sonne, Hitze und
            Blendung bieten und gleichzeitig Energieeffizienz, Wohnkomfort und
            architektonische Qualität verbessern. Unsere Systeme sind auf die
            klimatischen Bedingungen in der Schweiz ausgelegt und überzeugen
            durch Langlebigkeit, Funktionalität und zeitloses Design.
          </p>

          <h2>Unsere Leistungen im Überblick</h2>
          <p>
            Von der ersten Beratung bis zur langfristigen Wartung begleiten wir
            unsere Kunden zuverlässig. Sonnen Raffstoren GmbH steht für
            ganzheitliche Lösungen, bei denen Technik, Design und Service
            perfekt aufeinander abgestimmt sind. Besonders in der Schweiz, wo
            Wetterbedingungen und Bauvorschriften hohe Anforderungen stellen,
            ist eine professionelle Planung entscheidend.
          </p>

          <ul>
            <li>Individuelle Beratung und Beschattungskonzepte</li>
            <li>Vor-Ort-Messungen in der gesamten Schweiz</li>
            <li>Maßanfertigung von Raffstoren und Rollläden</li>
            <li>Fachgerechte Montage und Inbetriebnahme</li>
            <li>Wartung, Service und Reparaturen</li>
          </ul>

          <h3>Raffstoren – Präzise Licht- und Wärmeregulierung</h3>
          <p>
            Raffstoren gehören zu den effizientesten außenliegenden
            Sonnenschutzsystemen. Durch verstellbare Lamellen kann der
            Lichteinfall exakt gesteuert werden, ohne den Raum vollständig zu
            verdunkeln. In der Schweiz, wo sommerliche Überhitzung zunehmend ein
            Thema ist, leisten Raffstoren einen wichtigen Beitrag zur Reduktion
            des Kühlbedarfs.
          </p>

          <h4>Technische Eigenschaften</h4>
          <ol>
            <li>Windstabile Konstruktionen für exponierte Lagen</li>
            <li>Korrosionsgeschützte Materialien</li>
            <li>Verschiedene Lamellenprofile</li>
            <li>Große Auswahl an Farben und Oberflächen</li>
          </ol>

          <h3>Rollläden und individuelle Außenbeschattung</h3>
          <p>
            Neben Raffstoren bieten wir Rollläden und weitere maßgeschneiderte
            Beschattungslösungen an. Diese eignen sich besonders für
            Anwendungen, bei denen zusätzlicher Sichtschutz, Verdunkelung oder
            Einbruchhemmung gefragt sind. Unsere Systeme werden exakt an die
            Fassade und Nutzung angepasst.
          </p>

          <h4>Motorisierung & Smart-Home-Integration</h4>
          <p>
            Moderne Gebäude in der Schweiz setzen zunehmend auf Automatisierung.
            Unsere Lösungen können mit elektrischen Antrieben, Wind- und
            Sonnensensoren sowie Smart-Home- und Gebäudeautomationssystemen
            kombiniert werden. So reagiert der Sonnenschutz automatisch auf
            Wetterbedingungen und Tageslicht.
          </p>

          <h2>Qualität als Unternehmensphilosophie</h2>
          <p>
            Qualität steht bei Sonnen Raffstoren GmbH im Mittelpunkt. Wir
            verwenden geprüfte Materialien und arbeiten mit ausgewählten
            Zulieferern. Jedes Produkt wird auf Funktion, Stabilität und
            Witterungsbeständigkeit geprüft, bevor es montiert wird. Unsere
            Montageteams sind geschult und verfügen über langjährige Erfahrung
            im Schweizer Markt.
          </p>

          <h3>Nachhaltigkeit und Energieeffizienz</h3>
          <p>
            Außenliegender Sonnenschutz ist ein wesentlicher Bestandteil
            nachhaltiger Gebäudekonzepte. Durch die Reduktion der solaren
            Wärmeeinträge können Klimaanlagen entlastet oder ganz vermieden
            werden. Dies senkt den Energieverbrauch und unterstützt die
            Klimaziele in der Schweiz.
          </p>

          <ul>
            <li>Reduzierung der sommerlichen Überhitzung</li>
            <li>Optimale Nutzung von Tageslicht</li>
            <li>Geringerer Stromverbrauch für Kühlung</li>
            <li>Lange Lebensdauer der Systeme</li>
          </ul>

          <h4>Materialien und Umwelt</h4>
          <p>
            Wo immer möglich, setzen wir auf recycelbare Materialien und
            langlebige Konstruktionen. Nachhaltigkeit bedeutet für uns nicht nur
            Energieeinsparung, sondern auch Verantwortung gegenüber Ressourcen
            und Umwelt.
          </p>

          <h2>Projektgeschäft und Zusammenarbeit</h2>
          <p>
            Für Architekten, Bauleiter und Generalunternehmer bieten wir
            projektbezogene Leistungen an. Wir unterstützen bei Ausschreibungen,
            Detailplanung und Koordination und sorgen für eine reibungslose
            Umsetzung – vom Einfamilienhaus bis zum großen Gewerbeobjekt in der
            Schweiz.
          </p>

          <h5>Typische Projekte</h5>
          <ul>
            <li>Einfamilienhäuser und Wohnüberbauungen</li>
            <li>Büro- und Verwaltungsgebäude</li>
            <li>Öffentliche Gebäude und Schulen</li>
            <li>Industrie- und Gewerbebauten</li>
          </ul>

          <h6>Kundenzufriedenheit & Service</h6>
          <p>
            Transparente Angebote, realistische Zeitpläne und zuverlässiger
            Service sind für uns selbstverständlich. Auch nach der Installation
            stehen wir unseren Kunden in der Schweiz für Wartung und Support zur
            Verfügung.
          </p>

          <hr />

          <p>
            <strong>Sonnen Raffstoren GmbH</strong> – Ihr zuverlässiger Partner
            für langlebigen, energieeffizienten und architektonisch
            anspruchsvollen Sonnenschutz in der Schweiz.
          </p>

          <small>
            Keywords: Schweiz, Raffstoren, Rollläden, Außenbeschattung,
            Sonnenschutz, Smart Home, Energieeffizienz, Romanshorn,
            Fassadenlösungen, Storen, Insektenschutz, Markisen, individuelle Planung, Montage, Wartung, nachhaltige Beschattung, Projektgeschäft, Architektenservice.
          </small>
        </div>
        <div style={{ display: 'none' }}>
          <h1>Raffstoren — Präzise Außenbeschattung für optimale Lichtsteuerung</h1>
          Raffstoren sind außenliegende Jalousien mit verstellbaren Lamellen,
          die eine präzise Regulierung von Licht und Wärme ermöglichen. Sie
          schützen vor direkter Sonneneinstrahlung, reduzieren Blendung und
          tragen zur Energieeffizienz bei, indem sie den Kühlbedarf senken.

          <h2>Markisen — Elegante Terrassenbeschattung und Außenbereiche</h2>
          Markisen bieten flexiblen Sonnenschutz für Terrassen, Balkone und
          Außenflächen. Mit verschiedenen Größen, Farben und Materialien passen
          sie sich perfekt an die Architektur an und schaffen gemütliche
          Außenbereiche, die vor Hitze und UV-Strahlen schützen.

          <h3>Insektenschutz — Effektiver Schutz vor Insekten</h3>
          Insektenschutzsysteme halten unerwünschte Insekten draußen, während
          sie frische Luft hereinlassen. Kombinierbar mit Raffstoren und
          Markisen für ganzjährigen Komfort und Schutz vor Mücken, Fliegen und
          anderen Insekten.
        </div>
        `}</html>
      </Helmet>
      <section
        className="relative flex flex-col md:flex-row items-center justify-between min-h-svh text-white px-smallPx md:px-mediumPx lg:px-largePx"
        style={{
          backgroundImage: isSmallScreen
            ? `url(${mobileBg})`
            : `url(${backgroundImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      >
        <div className="absolute inset-0 bg-black bg-opacity-50"></div>

        <div className="relative z-10 flex w-full max-w-7xl mx-auto gap-10">
          <div className="w-full md:w-3/5 flex flex-col justify-center space-y-[10px] md:space-y-[20px] h-svh">
            <motion.h1
              className="text-[64px] md:text-mediumFont lg:text-largeFont xl:text-extraLargeFont font-bold leading-none text-left tracking-tight"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.3, ease: 'easeOut' }}
            >
              <span className="font-serif font-[400]">
                {t('hero.title.part1')}
              </span>{' '}
              <span className="font-saol italic text-primary">
                {t('hero.title.part2')}
              </span>{' '}
              {isSmallScreen ? <br /> : <></>}
              <span className="font-serif font-[400]">
                {t('hero.title.part3')}
              </span>
            </motion.h1>

            <div className="flex flex-col-reverse lg:flex-row gap-[20px]">
              <div>
                <Button
                  text={t('hero.button')}
                  className="text-white hover:text-black"
                  onClick={() => navigate('/about')}
                />
              </div>
              <p className="text-[14px] md:text-[16px] leading-relaxed text-left w-[80%] md:w-full">
                {t('hero.description')}
              </p>
            </div>
          </div>

          <div className="hidden md:flex md:w-2/5 justify-center items-center relative">
            <div
              className="absolute w-[320px] lg:w-[390px] h-[290px] lg:h-[330px]"
              style={{
                backgroundImage: `url(${rightSideImage})`,
                backgroundSize: 'contain',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
              }}
            />
            <div
              className={`relative z-10 text-primary pb-6 text-[18px] ${featureTextSize} text-left`}
            >
              <div className="flex flex-row gap-2 items-center">
                <DoubleTickIcon />
                <h3>{t('hero.features.energy')}</h3>
              </div>
              <div className="flex flex-row gap-2 items-center">
                <DoubleTickIcon />
                <h3>{t('hero.features.custom')}</h3>
              </div>
              <div className="flex flex-row gap-2 items-center">
                <DoubleTickIcon />
                <h3>{t('hero.features.design')}</h3>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default HeroSection;
